import { TFunction } from "i18next";
import { FieldValues, Path, UseFormReturn } from "react-hook-form";

export const ALPHABETIC_ONLY_PATTERN = /^[A-Za-z ]+$/;

export function sanitizeAlphabeticInput(value: string) {
  return value.replace(/[^A-Za-z ]/g, "");
}
export const ALPHANUMERIC_ONLY_PATTERN = /^[A-Za-z0-9\s]+$/;

export function sanitizeAlphanumericInput(value: string) {
  return value.replace(/[^A-Za-z0-9\s]/g, "");
}

export const EMERGENCY_FIELD_NAMES = [
  "emergency_contact_person_name",
  "emergency_phone_number",
  "emergency_contact_relationship",
  "emergency_contact_relationship_other",
] as const;

export function hasAnyEmergencyContactValue(values: {
  emergency_contact_person_name?: string;
  emergency_phone_number?: string;
  emergency_contact_relationship?: string;
}) {
  return !!(
    values.emergency_contact_person_name?.trim() ||
    values.emergency_phone_number?.trim() ||
    values.emergency_contact_relationship
  );
}

type EmergencyContactFormValues = FieldValues & {
  emergency_contact_person_name?: string;
  emergency_phone_number?: string;
  emergency_contact_relationship?: string;
  emergency_contact_relationship_other?: string;
  phone_number?: string;
};

export function triggerEmergencyContactValidation<
  T extends EmergencyContactFormValues,
>(
  form: UseFormReturn<T>,
  t: TFunction,
  options: { showMissing?: boolean } = {},
) {
  const values = form.getValues();
  const personNamePath = "emergency_contact_person_name" as Path<T>;
  const relationshipPath = "emergency_contact_relationship" as Path<T>;
  const phonePath = "emergency_phone_number" as Path<T>;
  const relationshipOtherPath =
    "emergency_contact_relationship_other" as Path<T>;
  const touched = form.formState.touchedFields as {
    emergency_contact_person_name?: unknown;
    emergency_phone_number?: unknown;
    emergency_contact_relationship?: unknown;
    emergency_contact_relationship_other?: unknown;
  };
  const personName = values.emergency_contact_person_name?.trim();
  const relationship = values.emergency_contact_relationship;
  const phone = values.emergency_phone_number?.trim();
  const patientPhone = values.phone_number?.trim();
  const fields = [personName, relationship, phone];
  const shouldShowMissing =
    options.showMissing ||
    touched.emergency_contact_person_name ||
    touched.emergency_phone_number ||
    touched.emergency_contact_relationship;

  if (fields.some(Boolean)) {
    if (!personName && shouldShowMissing) {
      form.setError(personNamePath, {
        type: "manual",
        message: t("field_required"),
      });
    } else {
      form.clearErrors(personNamePath);
    }
    if (!relationship && shouldShowMissing) {
      form.setError(relationshipPath, {
        type: "manual",
        message: t("field_required"),
      });
    } else {
      form.clearErrors(relationshipPath);
    }
    if (!phone && shouldShowMissing) {
      form.setError(phonePath, {
        type: "manual",
        message: t("field_required"),
      });
    } else if (patientPhone && phone === patientPhone) {
      form.setError(phonePath, {
        type: "manual",
        message: t(
          "emergency_contact_number_must_be_different_from_patient_phone_number",
        ),
      });
    } else {
      form.clearErrors(phonePath);
    }
  } else {
    form.clearErrors(personNamePath);
    form.clearErrors(phonePath);
    form.clearErrors(relationshipPath);
    form.clearErrors(relationshipOtherPath);
  }

  if (relationship === "other") {
    const otherValue = values.emergency_contact_relationship_other?.trim();
    if (!otherValue && touched.emergency_contact_relationship_other) {
      form.setError(relationshipOtherPath, {
        type: "manual",
        message: t("field_required"),
      });
    }
  }
}

export function validateAlphabeticOtherField<T extends object>(
  form: UseFormReturn<T>,
  fieldName: Path<T>,
  value: string | undefined,
  t: TFunction,
  required: boolean,
) {
  const trimmed = value?.trim() ?? "";

  if (!trimmed) {
    if (required) {
      form.setError(fieldName, {
        type: "manual",
        message: t("field_required"),
      });
    } else {
      form.clearErrors(fieldName);
    }
    return;
  }

  if (!ALPHABETIC_ONLY_PATTERN.test(trimmed)) {
    form.setError(fieldName, {
      type: "manual",
      message: t("alphabetic_characters_only"),
    });
    return;
  }

  form.clearErrors(fieldName);
}

export function validateAlphanumericOtherField<T extends object>(
  form: UseFormReturn<T>,
  fieldName: Path<T>,
  value: string | undefined,
  t: TFunction,
  required: boolean,
) {
  const trimmed = value?.trim() ?? "";

  if (!trimmed) {
    if (required) {
      form.setError(fieldName, {
        type: "manual",
        message: t("field_required"),
      });
    } else {
      form.clearErrors(fieldName);
    }
    return;
  }

  if (!ALPHANUMERIC_ONLY_PATTERN.test(trimmed)) {
    form.setError(fieldName, {
      type: "manual",
      message: t("alphanumeric_characters_only"),
    });
    return;
  }

  form.clearErrors(fieldName);
}
